import { cn } from "lib/utils";
import React, { useEffect, useState } from "react";

import arrowBigUp from "assets/images/totem/arrow-big-up.svg";
import deleteAlphanumeric from "assets/images/totem/delete-alphanumeric.svg";
import deleteNumeric from "assets/images/totem/delete-numeric.svg";

export type KeyboardKey = {
	label?: string;
	value?: string;
	icon?: string;
	muted?: boolean;
	className?: string;
};

type KeyboardProps = {
	type: "alphanumeric" | "numeric";
	onKeyPress: (key: KeyboardKey) => void;
};

const keyboardRows: KeyboardKey[][] = [
	["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"].map((value) => ({
		label: value,
		value,
	})),
	["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"].map((value) => ({
		label: value,
		value,
	})),
	["a", "s", "d", "f", "g", "h", "j", "k", "l"].map((value) => ({
		label: value,
		value,
	})),
	[
		{ icon: arrowBigUp, value: "shift" },
		...["z", "x", "c", "v", "b", "n", "m"].map((value) => ({
			label: value,
			value,
		})),
		{ icon: deleteAlphanumeric, value: "delete" },
	],
	[
		{
			label: "123",
			value: "numbers",
			muted: true,
			className: "max-w-[200px] text-[18px]",
		},
		{ label: "espaço", value: " ", className: "text-[18px]" },
		{
			label: "concluído",
			value: "done",
			muted: true,
			className: "max-w-[200px] text-[18px]",
		},
	],
];

const numericRows: KeyboardKey[][] = [
	["1", "2", "3"].map((value) => ({ label: value, value })),
	["4", "5", "6"].map((value) => ({ label: value, value })),
	["7", "8", "9"].map((value) => ({ label: value, value })),
	[
		{ label: "", value: "noop", muted: true },
		{ label: "0", value: "0" },
		{ icon: deleteNumeric, value: "delete", muted: true },
	],
];

export const TotemKeyboard: React.FC<KeyboardProps> = ({
	type,
	onKeyPress,
}) => {
	const rows = type === "numeric" ? numericRows : keyboardRows;
	const [shiftActive, setShiftActive] = useState(false);

	useEffect(() => {
		setShiftActive(false);
	}, [type]);

	const isLetterKey = (key: KeyboardKey) =>
		type === "alphanumeric" && !!key.value && /^[a-z]$/i.test(key.value);

	const getLetterValue = (key: KeyboardKey) =>
		shiftActive ? key.value?.toUpperCase() : key.value?.toLowerCase();

	const getLabel = (key: KeyboardKey) =>
		isLetterKey(key) ? getLetterValue(key) : key.label;

	const handleShiftClick = () => {
		setShiftActive((active) => !active);
	};

	const handleKeyClick = (key: KeyboardKey) => {
		if (isLetterKey(key)) {
			onKeyPress({
				...key,
				label: getLetterValue(key),
				value: getLetterValue(key),
			});
			return;
		}

		onKeyPress(key);
	};

	return (
		<div
			className={cn(
				"flex h-[401px] w-full flex-col gap-[10px] overflow-hidden bg-[#E5E7EB] px-3 py-4 max-[1023px]:h-[360px] max-[1023px]:gap-2 max-[639px]:h-[300px] max-[639px]:gap-1 max-[639px]:px-2 max-[639px]:py-2",
			)}
		>
			{rows.map((row, rowIndex) => (
				<div
					key={`keyboard-row-${rowIndex}`}
					className={cn(
						"flex min-h-0 flex-1 items-center justify-center gap-[10px] overflow-hidden max-[639px]:gap-1",
					)}
				>
					{row.map((key, keyIndex) => (
						<button
							key={`${key.value}-${keyIndex}`}
							type="button"
							onClick={
								key.value === "shift"
									? handleShiftClick
									: () => handleKeyClick(key)
							}
							aria-label={
								key.value === "shift"
									? shiftActive
										? "Desativar letras maiúsculas"
										: "Ativar letras maiúsculas"
									: undefined
							}
							aria-pressed={
								key.value === "shift" ? shiftActive : undefined
							}
							className={cn(
								"flex h-full min-w-0 flex-1 items-center justify-center overflow-hidden rounded-[8px] bg-white font-sans text-[27px] font-normal leading-normal text-[#111827] transition-colors hover:bg-slate-100 max-[1023px]:text-[23px] max-[639px]:rounded-[4px] max-[639px]:text-[18px]",
								key.muted && "bg-[#9CA3AF] hover:bg-[#9CA3AF]",
								key.value === "shift" &&
									shiftActive &&
									"bg-[#9CA3AF] hover:bg-[#9CA3AF]",
								key.className,
							)}
						>
							{key.icon ? (
								<img
									src={key.icon}
									alt=""
									className={cn(
										"block h-[26px] w-[26px] max-[639px]:h-5 max-[639px]:w-5",
									)}
								/>
							) : (
								getLabel(key)
							)}
						</button>
					))}
				</div>
			))}
		</div>
	);
};
